import React, { useState, useEffect } from 'react';
import { Typography, Button } from '@mui/material';
import { message, Typography as AntTypography, Modal } from 'antd';
import { IconCloudStorm } from '@tabler/icons-react';
import api from '../../../authentication/axiosInstance';
import styles from './StormStrategyPage.module.css';
import dayjs from 'dayjs';
import Header from '../../../components/client-care/storm-strategy/header/header';
import WeatherConditionsCard from '../../../components/client-care/storm-strategy/weather-conditions-card/weather-conditions-card';
import SelectedSitesCard from '../../../components/client-care/storm-strategy/selected-sites-card/selected-sites-card';
import MessageCard from '../../../components/client-care/storm-strategy/message-card/message-card';
import AvailableSitesCard from '../../../components/client-care/storm-strategy/available-sites-card/available-sites-card';
import IncomingStormsCard from '../../../components/client-care/storm-strategy/incoming-storms-card/incoming-storms-card';
import ConfirmationModal from '../../../components/client-care/storm-strategy/confirmation-modal/confirmation-modal';
import WeatherModal from '../../../components/client-care/storm-strategy/weather-modal/weather-modal';
import CurrentWeather from '../../../components/client-care/storm-strategy/current-weather/current-weather';
import HourlyForecast from '../../../components/client-care/storm-strategy/hourly-forecast/hourly-forecast';
import EventModal from '../../../components/client-care/storm-strategy/event-modal/event-modal';
import RenderEvents from '../../../components/client-care/storm-strategy/render-events/render-events';

const { Text } = AntTypography;

// Environment variables
const TEST_MODE_EMAIL = process.env.REACT_APP_TEST_MODE_EMAIL;
const DEFAULT_CONTACT_EMAIL = process.env.REACT_APP_DEFAULT_CONTACT_EMAIL;

const WIND_DIRECTIONS = {
  0: 'N', 45: 'NE', 90: 'E', 135: 'SE',
  180: 'S', 225: 'SW', 270: 'W', 315: 'NW', 360: 'N'
};

const inchesToCm = (inches) => inches * 2.54;
const fahrenheitToCelsius = (f) => Math.round((f - 32) * 5 / 9);

const StormStrategyPage = () => {
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [regions, setRegions] = useState([]);
  const [sites, setSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [customMessage, setCustomMessage] = useState('');
  const [weatherData, setWeatherData] = useState(null);
  const [loading, setLoading] = useState({
    sites: false,
    weather: false,
    sending: false
  });
  const [searchText, setSearchText] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [selectedWeather, setSelectedWeather] = useState(null);
  const [showWeatherModal, setShowWeatherModal] = useState(false);
  const [isTestMode, setIsTestMode] = useState(false);
  const [events, setEvents] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [generatingMessage, setGeneratingMessage] = useState(false);

  // Add keyboard shortcut handler
  useEffect(() => {
    const handleKeyPress = (event) => {
      // Ctrl + Alt + T to toggle test mode
      if (event.ctrlKey && event.altKey && event.key === 't') {
        if (!TEST_MODE_EMAIL) {
          message.error('Test mode is not configured. Please set TEST_MODE_EMAIL first.');
          return;
        }
        setIsTestMode(prev => {
          const newMode = !prev;
          message.success(`Test mode ${newMode ? 'enabled' : 'disabled'}`, 1);
          return newMode;
        });
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  // fetch regions on component mount
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const response = await api.get('/sites/site_regions/');
        setRegions(response.data);
      } catch (error) {
        console.error('Error fetching regions:', error);
        message.error('Failed to fetch regions');
      }
    };
    fetchRegions();
  }, []);

  // fetch sites when region changes
  useEffect(() => {
    const fetchSites = async () => {
      if (!selectedRegion) {
        setSites([]);
        return;
      }
      
      setLoading(prev => ({ ...prev, sites: true }));
      try {
        const response = await api.get('/sites/', {
          params: { region: selectedRegion }
        });
        const filteredSites = response.data.filter(site => 
          site.region === selectedRegion && 
          site.contract_status === 'Confirmed'
        );
        setSites(filteredSites);
      } catch (error) {
        console.error('Error fetching sites:', error);
        message.error('Failed to fetch sites');
      } finally {
        setLoading(prev => ({ ...prev, sites: false }));
      }
    };
    fetchSites();
  }, [selectedRegion]);

  // fetch weather data when region changes
  useEffect(() => {
    const fetchWeatherData = async () => {
      if (!selectedRegion) return;
      
      setLoading(prev => ({ ...prev, weather: true }));
      try {
        const response = await api.get(`/weatherworks/forecast/${selectedRegion}/`);
        if (response.data.success) {
          setWeatherData({
            ...response.data.forecast,
            location: response.data.location
          });
        } else {
          const fallbackResponse = await api.get('/weatherworks/forecast/Bolton/');
          if (fallbackResponse.data.success) {
            setWeatherData({
              ...fallbackResponse.data.forecast,
              location: fallbackResponse.data.location
            });
          } else {
            message.error('Failed to fetch weather data');
          }
        }
      } catch (error) {
        try {
          const fallbackResponse = await api.get('/weatherworks/forecast/Bolton/');
          if (fallbackResponse.data.success) {
            setWeatherData({
              ...fallbackResponse.data.forecast,
              location: fallbackResponse.data.location
            });
          } else {
            message.error('Failed to fetch weather data');
          }
        } catch (fallbackError) {
          console.error('Error fetching weather data:', error);
          message.error('Failed to fetch weather data');
        }
      } finally {
        setLoading(prev => ({ ...prev, weather: false }));
      }
    };
    fetchWeatherData();
  }, [selectedRegion]);

  // Add new useEffect for fetching events
  useEffect(() => {
    const fetchEvents = async () => {
      if (!selectedRegion) {
        setEvents([]);
        return;
      }

      setLoadingEvents(true);
      
      try {
        const response = await api.get(`/weatherworks/fetch-events-by-region/${encodeURIComponent(selectedRegion)}/`);
        
        if (response.data.success) {
          const eventData = response.data.events || [];
          setEvents(eventData);
        } else {
          console.error('Failed to fetch events:', response.data.message);
          setEvents([]);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
        message.error('Failed to fetch weather events');
        setEvents([]);
      } finally {
        setLoadingEvents(false);
      }
    };

    fetchEvents();
  }, [selectedRegion]);

  const handleSiteSelect = async (site) => {
    try {
      // Fetch only associated contacts for the site
      const response = await api.get(`/sites/${site.site_id}/associated_contacts/`);
      // If no contacts and test mode is off, add default contact
      if (response.data.length === 0 && !isTestMode) {
        site.contacts = [{
          first_name: 'Default',
          last_name: 'Contact',
          email: DEFAULT_CONTACT_EMAIL || 'default@example.com'
        }];
      } else {
        site.contacts = response.data;
      }
      setSelectedSites(prev => [...prev, site]);
    } catch (error) {
      console.error('Error fetching site contacts:', error);
      message.error('Failed to fetch site contacts');
      // Add default contact on error
      setSelectedSites(prev => [...prev, {
        ...site,
        contacts: [{
          first_name: 'Default',
          last_name: 'Contact',
          email: DEFAULT_CONTACT_EMAIL || 'default@example.com'
        }]
      }]);
    }
  };

  const handleSiteRemove = (siteId) => {
    setSelectedSites(prev => prev.filter(site => site.site_id !== siteId));
  };

  const handleSubmit = async () => {
    if (!selectedRegion || selectedSites.length === 0 || !customMessage.trim()) {
      message.error('Please fill in all required fields');
      return;
    }

    setLoading(prev => ({ ...prev, sending: true }));
    try {
      await api.post('/storm-strategy/', {
        region: selectedRegion,
        sites: selectedSites.map(site => site.id),
        message: customMessage,
        weather_data: weatherData
      });
      message.success('Storm strategy sent successfully');
      // reset form
      setSelectedSites([]);
      setCustomMessage('');
    } catch (error) {
      console.error('Error sending storm strategy:', error);
      message.error('Failed to send storm strategy');
    } finally {
      setLoading(prev => ({ ...prev, sending: false }));
    }
  };

  const getWindDirection = (degrees) => {
    const directions = Object.entries(WIND_DIRECTIONS);
    return directions.reduce((closest, [deg, dir]) => {
      return Math.abs(deg - degrees) < Math.abs(closest[0] - degrees) ? [deg, dir] : closest;
    })[1];
  };

  const handleSearch = (value) => {
    setSearchText(value);
  };

  const clearSelectedSites = () => {
    Modal.confirm({
      title: 'Clear Selected Sites',
      content: 'Are you sure you want to remove all selected sites?',
      onOk: () => setSelectedSites([]),
      okText: 'Yes, clear all',
      cancelText: 'No, keep them'
    });
  };

  const handleSubmitConfirm = () => {
    setShowConfirmModal(true);
  };

  const handleSubmitFinal = async () => {
    setShowConfirmModal(false);
    setLoading(prev => ({ ...prev, sending: true }));
    try {
      // Map sites to ensure each has at least one contact
      const sitesWithContacts = selectedSites.map(site => ({
        ...site,
        contacts: isTestMode && TEST_MODE_EMAIL ? [{
          first_name: 'Test',
          last_name: 'Mode',
          email: TEST_MODE_EMAIL
        }] : (site.contacts?.length ? site.contacts : [{
          first_name: 'Default',
          last_name: 'Contact',
          email: DEFAULT_CONTACT_EMAIL || 'default@example.com'
        }])
      }));

      await api.post('/weatherworks/storm-strategy/', {
        region: selectedRegion,
        sites: sitesWithContacts,
        message: customMessage,
        weather_data: weatherData,
        test_mode: isTestMode
      });
      message.success('Storm strategy sent successfully to all selected sites');
      setSelectedSites([]);
      setCustomMessage('');
    } catch (error) {
      console.error('Error sending storm strategy:', error);
      message.error(
        'Failed to send storm strategy. Please try again or contact support if the problem persists.'
      );
    } finally {
      setLoading(prev => ({ ...prev, sending: false }));
    }
  };

  const retryWeatherFetch = () => {
    setRetryCount(prev => prev + 1);
  };

  const columns = [
    {
      title: 'Site Name',
      dataIndex: 'site_name',
      key: 'site_name',
      sorter: (a, b) => a.site_name.localeCompare(b.site_name),
      render: (text) => <Text strong>{text}</Text>,
      filteredValue: [searchText],
      onFilter: (value, record) => 
        record.site_name.toLowerCase().includes(value.toLowerCase())
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <Button
          size="small"
          onClick={() => handleSiteSelect(record)}
          disabled={selectedSites.some(site => site.site_id === record.site_id)}
          className={styles.actionButton}
        >
          Select
        </Button>
      )
    }
  ];

  const handleWeatherClick = (weather, isCurrentWeather = false) => {
    const weatherDetails = isCurrentWeather ? {
      ...weather,
      time: 'Current',
      isCurrentWeather: true
    } : weather;
    setSelectedWeather(weatherDetails);
    setShowWeatherModal(true);
  };

  const renderWeatherModal = () => {
    if (!selectedWeather) return null;

    const isExtremeWeather = 
      selectedWeather.precipitation?.probability > 70 ||
      selectedWeather.wind?.speed > 50;

    return (
      <WeatherModal 
        selectedWeather={selectedWeather}
        showWeatherModal={showWeatherModal}
        setShowWeatherModal={setShowWeatherModal}
        isExtremeWeather={isExtremeWeather}
        getWindDirection={getWindDirection}
      />
    );
  };

  const renderCurrentWeather = () => {
    if (!weatherData?.current) return null;
    
    const isExtremeWeather = 
      weatherData.current.precipitation.probability > 70 ||
      weatherData.current.wind.speed > 50;
    
    return (
      <CurrentWeather
        handleWeatherClick={handleWeatherClick}
        weatherData={weatherData}
        isExtremeWeather={isExtremeWeather}
        getWindDirection={getWindDirection}
      />
    );
  };

  const renderHourlyForecast = () => {
    if (!weatherData?.hourly?.length) return null;

    // get current hour in est
    const currentTime = dayjs().tz('America/Toronto');
    const currentHour = currentTime.hour();
    const nextHour = currentHour + 1;  // start from next hour

    // filter and get next 8 hours starting from next hour
    const nextHours = weatherData.hourly
      .filter(hour => {
        const hourTime = parseInt(hour.time.split(':')[0]);
        return hourTime >= nextHour;  // compare with next hour instead of current
      })
      .slice(0, 8);

    return (
      <HourlyForecast 
        handleWeatherClick={handleWeatherClick}
        nextHours={nextHours}
      />
    );
  };

  const selectAllSites = () => {
    // filter out sites that are already selected
    const newSites = sites.filter(
      site => !selectedSites.some(selected => selected.site_id === site.site_id)
    );
    setSelectedSites(prev => [...prev, ...newSites]);
  };

  const generateAIMessage = async () => {
    if (!selectedRegion) {
      message.error('Please select a region first');
      return;
    }

    setGeneratingMessage(true);
    try {
      // send events data instead of threats
      const currentEvents = loadingEvents ? [] : events;
      
      const response = await api.post('/weatherworks/generate-message/', {
        region: selectedRegion,
        weather_data: weatherData,
        events: currentEvents
      });

      if (response.data.success) {
        setCustomMessage(response.data.message);
        message.success('Message generated successfully');
      } else {
        message.error('Failed to generate message');
      }
    } catch (error) {
      console.error('Error generating message:', error);
      message.error('Failed to generate message');
    } finally {
      setGeneratingMessage(false);
    }
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setShowEventModal(true);
  };

  const renderEventModal = () => {
    if (!selectedEvent) return null;

    const startDate = dayjs(selectedEvent.startDate);
    const endDate = dayjs(selectedEvent.endDate);
    const duration = endDate.diff(startDate, 'hours', true);
    const isOngoing = selectedEvent.ongoing;

    const minTempC = selectedEvent.minTempF ? fahrenheitToCelsius(selectedEvent.minTempF) : null;
    const maxTempC = selectedEvent.maxTempF ? fahrenheitToCelsius(selectedEvent.maxTempF) : null;

    // Get weather icon based on primary weather
    

    return (
      <EventModal
        selectedEvent={selectedEvent}
        showEventModal={showEventModal}
        setShowEventModal={setShowEventModal}
        duration={duration}
      />
    );
  };

  const renderEvents = () => {
    if (loadingEvents) {
      return (
        <div className={styles.loadingState}>
          <IconCloudStorm size={24} className={styles.loadingIcon} />
          <Typography>Loading events data...</Typography>
        </div>
      );
    }

    if (events.length === 0) {
      return (
        <div className={styles.emptyState}>
          <IconCloudStorm size={24} />
          <Typography>No weather events for this region</Typography>
        </div>
      );
    }
    return (
      <RenderEvents events={events} handleEventClick={handleEventClick} />
    )
  };

  return (
    <div className={styles.container}>
      <Header />
      <div className={styles.content}>
        <div className={styles.leftPanel}>
          <WeatherConditionsCard 
            setSelectedRegion={setSelectedRegion}
            loading={loading}
            regions={regions}
            weatherData={weatherData}
            renderCurrentWeather={renderCurrentWeather}
            renderHourlyForecast={renderHourlyForecast}
          />
          <SelectedSitesCard 
            selectedSites={selectedSites}
            clearSelectedSites={clearSelectedSites}
            handleSiteRemove={handleSiteRemove}
          />
          <MessageCard
            customMessage={customMessage}
            setCustomMessage={setCustomMessage}
            generateAIMessage={generateAIMessage}
            generatingMessage={generatingMessage}
            selectedRegion={selectedRegion}
            selectedSites={selectedSites}
            handleSubmitConfirm={handleSubmitConfirm}
            loading={loading}
          />
        </div>
        <div className={styles.rightPanel}>
          <AvailableSitesCard 
            selectAllSites={selectAllSites}
            sites={sites}
            selectedSites={selectedSites}
            handleSearch={handleSearch}
            columns={columns}
            loading={loading}
          />
          <IncomingStormsCard
            renderEvents={renderEvents}
          />
        </div>
      </div>
      <ConfirmationModal 
        showConfirmModal={showConfirmModal}
        handleSubmitFinal={handleSubmitFinal}
        setShowConfirmModal={setShowConfirmModal}
        isTestMode={isTestMode}
        selectedSites={selectedSites}
        selectedRegion={selectedRegion}
        customMessage={customMessage}
      />
      {renderWeatherModal()}
      {renderEventModal()}
    </div>
  );
};

export default StormStrategyPage; 